import * as React from "react"
import { useTranslation } from "react-i18next"

import { useUIPreferences } from "Catalog/shared/components/UIPreferencesContext"
import useEditor from "shared/hooks/useEditor"
import { INutrition, IProduct } from "shared/hubrise/models"

import FoldableSection from "../FoldableSection"

import Composition from "./Composition"
import Producer from "./Producer"
import { Section, SectionHeader, SectionTitle } from "./Styles"
import Values from "./Values"

interface NutritionProps {
  product: IProduct
}

const EMPTY_NUTRITION: INutrition = {
  legal_name: null,
  ingredients: null,
  additives: null,
  allergens: null,
  per_100_values: null,
  alcohol_percentage: null,
  caffeine_mg_per_100ml: null,
  producer_name: null,
  producer_brand: null,
  producer_address: null,
  country_of_origin: null,
}

const Nutrition = ({ product }: NutritionProps): JSX.Element => {
  const { t } = useTranslation()
  const { editor } = useEditor()
  const { nutritionExpanded: expanded, setNutritionExpanded: setExpanded } = useUIPreferences()

  const nutrition = product.nutrition

  const updateNutrition = (patch: Partial<INutrition>) => {
    const current = nutrition ?? EMPTY_NUTRITION
    const newNutrition = { ...current, ...patch }
    const isNull = Object.values(newNutrition).every((value) => value === null)
    editor.product.update(product.id, "nutrition", isNull ? null : newNutrition)
  }

  return (
    <FoldableSection title={t("nutrition.component.title")} expanded={expanded} onToggle={() => setExpanded(!expanded)}>
      <Section>
        <SectionHeader>
          <SectionTitle>{t("nutrition.component.composition.title")}</SectionTitle>
        </SectionHeader>

        <Composition nutrition={nutrition} updateNutrition={updateNutrition} />
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t("nutrition.component.values.title")}</SectionTitle>
        </SectionHeader>

        <Values
          per100Values={nutrition?.per_100_values ?? null}
          onChange={(value) => updateNutrition({ per_100_values: value })}
        />
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>{t("nutrition.component.producer.title")}</SectionTitle>
        </SectionHeader>

        <Producer nutrition={nutrition} updateNutrition={updateNutrition} />
      </Section>
    </FoldableSection>
  )
}

export default Nutrition
